import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KbvPluginService } from "@/services/kbvPluginService";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import DateChips from "@/components/KbvKetQuaLightV32/DateChips";
import LeagueGroup from "@/components/KbvKetQuaLightV32/LeagueGroup";
import LeagueFilterModal from "@/components/KbvKetQuaLightV32/LeagueFilterModal";
import { FAVICON_SRC } from "@/components/KbvKetQuaLightV32/icons";
import { PAGE_LIMIT, buildDateChips, todayKey } from "@/components/KbvKetQuaLightV32/model";
import { useSiteOrigin } from "../useSiteOrigin";
import HotPicks from "./HotPicks";

function Spinner() {
  return (
    <div className="kbv-home-center__spinner">
      <div className="kbv-home-center__spinner-ring" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex kbv-col items-center justify-center">
      <img src={FAVICON_SRC} alt="KeoBongVip logo" className="w-[9rem] h-[7.375rem] my-4" />
      <p className="text-muted text-[0.75rem] text-center p-4">Không có trận đấu nào</p>
    </div>
  );
}

function tabQuery(type, date) {
  const base = {
    include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
    filterOdds: -1,
    cache: false,
    limit: PAGE_LIMIT,
  };
  if (type === "hot") {
    return { ...base, filterInclude: "date:all", status: "" };
  }
  if (type === "tructuyen") {
    return { ...base, filterInclude: `date:${date}`, status: "live" };
  }
  if (type === "ketqua") {
    return { ...base, filterInclude: `date:${date}`, status: "end" };
  }
  return { ...base, filterInclude: `date:${date}`, status: "" };
}

export default function MatchBoard({ type, filterOpen, onFilterClose, onFilterApplied }) {
  const origin = useSiteOrigin();
  const needsDate = type === "tatca" || type === "ketqua";
  const direction = type === "ketqua" ? "backward" : "forward";
  const days = useMemo(
    () => (needsDate ? buildDateChips(direction) : []),
    [needsDate, direction]
  );
  const [date, setDate] = useState(todayKey);
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null);
  const req = useRef(0);
  const busy = useRef(false);
  const sentinelRef = useRef(null);

  const filterAppliedRef = useRef(onFilterApplied);
  filterAppliedRef.current = onFilterApplied;

  useEffect(() => {
    setDate(todayKey());
    setSelectedIds(null);
    filterAppliedRef.current?.(false);
    setGroups([]);
    setPage(1);
    setTotal(0);
  }, [type]);

  const fetchPage = useCallback(
    async (pageNum, append) => {
      const my = ++req.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      const result = await KbvPluginService.fetchBangKeoMatches({
        ...tabQuery(type, date),
        page: pageNum,
      });
      if (my !== req.current) return;
      const payload = result?.data && !result?.error ? result.data : {};
      const rows = Array.isArray(payload.data) ? payload.data : [];
      setTotal(Number(payload.total) || 0);
      setPage(Number(payload.page) || pageNum);
      setGroups((prev) => (append ? [...prev, ...rows] : rows));
      setLoading(false);
      setLoadingMore(false);
    },
    [type, date]
  );

  useEffect(() => {
    setGroups([]);
    setPage(1);
    setTotal(0);
    fetchPage(1, false);
  }, [fetchPage]);

  const hasMore = page * PAGE_LIMIT < total;

  const loadMore = useCallback(() => {
    if (busy.current || loading || loadingMore || !hasMore) return;
    busy.current = true;
    fetchPage(page + 1, true).finally(() => {
      busy.current = false;
    });
  }, [fetchPage, hasMore, loading, loadingMore, page]);

  useEffect(() => {
    if (!hasMore || loading) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "120px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore, loading, groups.length]);

  const matchIds = useMemo(
    () =>
      groups.flatMap((group) =>
        (group?.matches || [])
          .map((match) => Number(match?.id))
          .filter((id) => Number.isFinite(id))
      ),
    [groups]
  );
  useLiveUpdates(matchIds);

  const leagueOptions = useMemo(
    () =>
      groups
        .map((group) => group?.league)
        .filter((league) => league && league.id != null),
    [groups]
  );

  const visible =
    selectedIds == null
      ? groups
      : groups.filter((group) => selectedIds.has(String(group?.league?.id)));

  return (
    <div className="kbv-kq-v32 kbv-home-center__board">
      {type === "hot" ? (loading ? <Spinner /> : <HotPicks />) : null}

      {needsDate ? (
        <DateChips
          days={days}
          value={date}
          direction={direction}
          onChange={(key) => {
            if (key === date) return;
            setDate(key);
            setSelectedIds(null);
            filterAppliedRef.current?.(false);
          }}
        />
      ) : null}

      {loading && type !== "hot" ? <Spinner /> : null}

      {!loading ? (
        <>
          <div className="kbv-home-center__list space-y-1 md:space-y-2">
            {visible.length === 0 ? (
              <EmptyState />
            ) : (
              visible.map((group) => {
                const league = group?.league || {};
                const id = String(league.id);
                return (
                  <LeagueGroup
                    key={id}
                    league={league}
                    matches={group?.matches}
                    isHome
                    siteOrigin={origin}
                  />
                );
              })
            )}
          </div>
          {hasMore ? (
            <div ref={sentinelRef} className="py-4 text-center">
              {loadingMore ? <Spinner /> : null}
            </div>
          ) : null}
        </>
      ) : null}

      {filterOpen ? (
        <LeagueFilterModal
          leagues={leagueOptions}
          selectedIds={selectedIds}
          onClose={onFilterClose}
          onApply={(next) => {
            setSelectedIds(next);
            filterAppliedRef.current?.(next != null);
            onFilterClose();
          }}
        />
      ) : null}
    </div>
  );
}
